import { AuthError, UpdateUserAttributeOutput } from "aws-amplify/auth";
import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PhoneInput } from "@/components/ui/phone-input";
import { zodResolver } from "@hookform/resolvers/zod";

import { SavePhoneFormData, savePhoneSchema } from "../types/save-phone.schema";

type SavePhoneFormProps = {
  onSubmit: (data: SavePhoneFormData) => Promise<UpdateUserAttributeOutput>;
};

export function SavePhoneForm({ onSubmit }: SavePhoneFormProps) {
  const form = useForm<SavePhoneFormData>({
    resolver: zodResolver(savePhoneSchema),
    defaultValues: {
      phoneNumber: "",
    },
  });

  const [countdown, setCountdown] = useState(0);
  const [isResendActive, setIsResendActive] = useState(true);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prevCount) => {
          if (prevCount <= 1) {
            clearInterval(timer);
            setIsResendActive(true);
            return 0;
          }
          return prevCount - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const handleSubmit = async (data: SavePhoneFormData) => {
    try {
      //this is the resend logic, first set it to empty then add the phone number again
      await onSubmit({
        phoneNumber: "",
      });
      await onSubmit({
        phoneNumber: data.phoneNumber,
      });
      // Start the cooldown after successful submission
      setCountdown(30);
      setIsResendActive(false);
    } catch (error) {
      if (error instanceof AuthError) {
        switch (error.name) {
          case "UpdateUserAttributesException":
            form.setError("phoneNumber", {
              type: "manual",
              message: "The phone number you entered is already in use.",
            });
            break;
          default:
            form.setError("root", {
              type: "manual",
              message: "The code you entered is incorrect. Please try again.",
            });
        }
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          type: "manual",
          message: "The code you entered is incorrect. Please try again.",
        });
      }
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <PhoneInput
                  {...field}
                  defaultCountry="GB"
                  international
                  placeholder="+44 7700 900000"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {form.formState.errors.root && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>
              {form.formState.errors.root.message}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex items-center gap-2">
          <Button
            type="submit"
            disabled={!form.formState.isValid || !isResendActive}
            isLoading={form.formState.isSubmitting}
            isAnimated
          >
            Send Verification Code
          </Button>
          {!isResendActive && (
            <p className="text-sm text-muted-foreground">
              Didn’t receive a code? Resend ({countdown})
            </p>
          )}
        </div>
      </form>
    </Form>
  );
}
